import { TIER_PART_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { Drawer } from '@silverfort/sf-components';
import React, { useEffect, useState } from 'react';
import { DEFAULT_PROTOCOLS_AD, TIER_SCOPES } from '../TierConsts';
import { ProtocolsSelector } from './protocolsSelector';
import ResourcesList from './ResourcesList';

const {
    JIT_SUPPORTED_PROTOCOLS,
    PRIVILEGED_DEFAULT_SERVICES
}: { JIT_SUPPORTED_PROTOCOLS: Record<string, string>; PRIVILEGED_DEFAULT_SERVICES: string[] } =
    require('@silverfort-nodelibs/common/utils/consts');

export interface DcsConfig {
    protocols: string[];
    services: string[];
}

interface DcsServicesDrawerProps {
    open: boolean;
    dcsNames: string[];
    dcsConfig: DcsConfig;
    updateDcsConfig: (config: DcsConfig) => void;
    onClose: () => void;
}

const DcsServicesDrawer: React.FC<DcsServicesDrawerProps> = ({
    open,
    dcsNames,
    dcsConfig,
    updateDcsConfig,
    onClose
}) => {
    const [shouldDisableSave, setShouldDisableSave] = useState(true);
    const [protocols, setProtocols] = useState<string[]>(dcsConfig?.protocols ?? DEFAULT_PROTOCOLS_AD);
    const [services, setServices] = useState<string[]>(dcsConfig?.services ?? []);

    useEffect(() => {
        if (open && dcsConfig) {
            setProtocols(dcsConfig.protocols ?? DEFAULT_PROTOCOLS_AD);
            setServices(dcsConfig.services ?? []);
        }
    }, [dcsConfig, open]);

    const resources = React.useMemo(
        () =>
            dcsNames.map((name) => ({
                name,
                type: TIER_PART_TYPES.SERVERS,
                id: name,
                protocols,
                services
            })),
        [dcsNames, protocols, services]
    );

    const handleSave = () => {
        updateDcsConfig({ protocols, services });
    };

    const onChangeProtocols = (next: string[]) => {
        if (next.length === 0) {
            setShouldDisableSave(true);
        } else {
            setShouldDisableSave(false);
        }
        setProtocols(next);
        if (!next.includes(JIT_SUPPORTED_PROTOCOLS.KERBEROS)) {
            setServices([]);
        } else if (services.length === 0) {
            setServices([...PRIVILEGED_DEFAULT_SERVICES]);
        }
    };

    const onChangeServices = (next: string[]) => {
        setShouldDisableSave(false);
        setServices(next);
        if (next.length === 0 && protocols.includes(JIT_SUPPORTED_PROTOCOLS.KERBEROS)) {
            setProtocols(protocols.filter((p) => p !== JIT_SUPPORTED_PROTOCOLS.KERBEROS));
        }
    };

    return (
        <Drawer
            footerActionTitle="Save"
            footerCloseTitle="Cancel"
            footerActionProps={{
                disabled: shouldDisableSave
            }}
            onAction={handleSave}
            onClose={onClose}
            variant="persistent"
            open={open}
            anchor="right"
            headerTitle="Domain Controllers"
            headerDescription="Select and edit protocols and services assigned to all domain controllers."
            className={'max-w-[680px]'}>
            <div className="h-full w-[632px] rounded-lg border border-sf-gray-100 flex flex-col">
                <div className="flex flex-1 h-full">
                    <div className="flex-1 w-full border-r border-sf-gray-100">
                        <ResourcesList
                            resources={resources}
                            disabled={true}
                            scope={TIER_SCOPES.DCS}
                            onSelect={undefined}
                            onDelete={undefined}
                            onClearAll={undefined}
                        />
                    </div>
                    <div className="w-[270px] min-w-[270px]">
                        <ProtocolsSelector
                            selectedProtocols={protocols}
                            onChangeProtocols={onChangeProtocols}
                            selectedServices={
                                protocols.includes(JIT_SUPPORTED_PROTOCOLS.KERBEROS) ? services : []
                            }
                            onChangeServices={onChangeServices}
                            defaultServices={PRIVILEGED_DEFAULT_SERVICES}
                            defaultProtocols={DEFAULT_PROTOCOLS_AD}
                            hideOptions={false}
                        />
                    </div>
                </div>
            </div>
        </Drawer>
    );
};

export default DcsServicesDrawer;
